/**
 * MessageFeedback — thumbs up/down on a completed assistant turn (Phase 5).
 *
 * Records a lightweight quality signal via `POST /chat/feedback` (upsert — one
 * rating per user+message; re-clicking the other thumb flips it). Optimistic:
 * the selected state applies immediately and reverts if the request fails.
 * Rendered only when a message id is known (persisted message, or the just-
 * streamed turn once the `done` event carried its id). Localized (chat ns).
 */
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { iconProps } from "@/lib/icons";
import { notify } from "@/lib/notify";
import { postChatFeedback, type ChatFeedbackRating } from "@/lib/garden-api";

export function MessageFeedback({
  messageId,
}: {
  messageId: string;
}): ReactElement {
  const { t } = useTranslation("chat");
  const [rating, setRating] = useState<ChatFeedbackRating | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (next: ChatFeedbackRating): Promise<void> => {
    if (pending) return;
    const prev = rating;
    setRating(next); // optimistic
    setPending(true);
    try {
      await postChatFeedback(messageId, next);
    } catch {
      // The optimistic state used to revert in SILENCE: the thumb turned green,
      // then turned itself grey again a moment later with no explanation, which
      // reads as the UI glitching rather than as a request failing. Say what
      // happened and offer the retry.
      setRating(prev);
      notify.error(t("feedback.error"), {
        id: `chat-feedback-${messageId}`,
        action: { label: t("errors.retry"), onClick: () => void submit(next) },
      });
    } finally {
      setPending(false);
    }
  };

  /**
   * Selected state carries a tinted plate as well as an ink change, so the
   * rating is not communicated by colour alone (WCAG 1.4.1) — `aria-pressed`
   * covers assistive tech, but a sighted user with low colour discrimination
   * gets the shape cue too.
   */
  const tone = (value: ChatFeedbackRating): string => {
    if (rating !== value) {
      return "text-[var(--g-ink-2)] hover:bg-[var(--g-primary-soft)] hover:text-foreground";
    }
    return value === "up"
      ? "bg-[color-mix(in_srgb,var(--g-success)_14%,transparent)] text-[var(--g-success)]"
      : "bg-[color-mix(in_srgb,var(--g-error)_14%,transparent)] text-[var(--g-error)]";
  };

  return (
    <span className="inline-flex items-center gap-0.5" data-testid="chat-feedback">
      <button
        type="button"
        disabled={pending}
        onClick={() => void submit("up")}
        aria-pressed={rating === "up"}
        aria-label={t("feedback.helpful")}
        title={t("feedback.helpful")}
        data-testid="chat-feedback-up"
        className={
          "inline-flex size-8 items-center justify-center rounded-[8px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-60 " +
          tone("up")
        }
      >
        <ThumbsUp {...iconProps("sm")} />
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={() => void submit("down")}
        aria-pressed={rating === "down"}
        aria-label={t("feedback.notHelpful")}
        title={t("feedback.notHelpful")}
        data-testid="chat-feedback-down"
        className={
          "inline-flex size-8 items-center justify-center rounded-[8px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-60 " +
          tone("down")
        }
      >
        <ThumbsDown {...iconProps("sm")} />
      </button>
    </span>
  );
}

export default MessageFeedback;
